/* DS Wood — floating cart drawer logic.
   Markup matches cart-drawer-body.liquid exactly so CSS applies consistently. */
(function () {
  'use strict';

  const root = document.querySelector('[data-cart-drawer]');
  if (!root) return;

  let currentCart = { items: [], item_count: 0 };

  const panel = root.querySelector('[data-cart-drawer-panel]');
  const body = root.querySelector('[data-cart-body]');
  const processStrip = root.querySelector('[data-cart-process]');
  const countBadges = document.querySelectorAll('.w-commerce-commercecartopenlinkcount, [data-cart-count]');

  /* ---------- Utils ---------- */
  function formatMoney(cents) {
    const n = cents / 100;
    const isInt = n === Math.round(n);
    return isInt
      ? Math.round(n).toLocaleString('fr-FR') + '€'
      : n.toFixed(2).replace('.', ',').replace(/,?0+$/, '') + '€';
  }
  function esc(str) {
    return String(str || '').replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /* ---------- Open / Close ---------- */
  function openDrawer() {
    panel.style.display = 'flex';
    requestAnimationFrame(function () { panel.classList.add('is-open'); });
    document.body.style.overflow = 'hidden';
  }
  function closeDrawer() {
    panel.classList.remove('is-open');
    setTimeout(function () {
      panel.style.display = 'none';
      document.body.style.overflow = '';
    }, 360);
  }

  /* ---------- Fetch + re-render ---------- */
  async function refreshCart(options) {
    const opts = options || {};
    try {
      const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      let cart = await res.json();
      cart = await cleanupOrphanGravures(cart);
      currentCart = cart;
      renderBody(cart);
      updateCount(cart.item_count, cart);
      if (opts.open) openDrawer();
    } catch (e) {
      console.error('refreshCart failed', e);
    }
  }

  function isUniqueVariantId(variantId) {
    const id = parseInt(variantId, 10);
    if (!id) return false;
    // Try matching by product_id via the current cart; otherwise by window list.
    const line = currentCart.items.find(function (i) { return i.variant_id === id || i.id === id; });
    if (line && isUniqueItem(line)) return true;
    // Fallback: check window.DSW_UNIQUE_VARIANT_IDS if exposed
    if (window.DSW_UNIQUE_VARIANT_IDS && window.DSW_UNIQUE_VARIANT_IDS.indexOf(id) !== -1) return true;
    return false;
  }

  function variantAlreadyInCart(variantId) {
    const id = parseInt(variantId, 10);
    if (!id) return false;
    return currentCart.items.some(function (i) { return i.variant_id === id || i.id === id; });
  }

  /* Remove any gravure-personnalisee line that has no matching plank with Gravure property. */
  async function cleanupOrphanGravures(cart) {
    const gravureLines = cart.items.filter(function (i) { return (i.handle || '') === 'gravure-personnalisee'; });
    const bearers = cart.items.filter(function (i) {
      return (i.handle || '') !== 'gravure-personnalisee' && i.properties && i.properties.Gravure;
    });
    // Sum quantities on each side (multiple lines each with qty 1 in practice, but be safe).
    const gravureQty = gravureLines.reduce(function (acc, i) { return acc + (i.quantity || 0); }, 0);
    const bearerQty = bearers.reduce(function (acc, i) { return acc + (i.quantity || 0); }, 0);
    if (gravureQty <= bearerQty) return cart;
    // Remove orphan gravure lines (from last to first). We drop all of them when there are no bearers.
    let updated = cart;
    const orphansToRemove = gravureLines.slice().reverse();
    let remaining = gravureQty - bearerQty;
    for (let i = 0; i < orphansToRemove.length && remaining > 0; i++) {
      const line = orphansToRemove[i];
      const newQty = Math.max(0, (line.quantity || 0) - remaining);
      remaining -= ((line.quantity || 0) - newQty);
      const r = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: line.key, quantity: newQty }),
      });
      updated = await r.json();
    }
    return updated;
  }

  function countVisibleItems(cart) {
    return (cart.items || []).reduce(function (acc, i) {
      if ((i.handle || '') === 'gravure-personnalisee') return acc;
      return acc + (i.quantity || 0);
    }, 0);
  }
  function updateCount(rawItemCount, cart) {
    const n = cart ? countVisibleItems(cart) : rawItemCount;
    countBadges.forEach(function (el) {
      el.textContent = n;
      el.dataset.count = n;
    });
    if (processStrip) processStrip.style.display = n > 0 ? '' : 'none';
  }

  /* ---------- "Produit unique" detection ---------- */
  const RESTOCKABLE_HANDLES = ['baume-bois', 'gravure-personnalisee', 'plaquette-personnalisee'];
  function isUniqueItem(item) {
    const handle = (item.handle || '').toLowerCase();
    if (RESTOCKABLE_HANDLES.indexOf(handle) !== -1) return false;
    if (window.DSW_UNIQUE_PRODUCT_IDS && window.DSW_UNIQUE_PRODUCT_IDS.indexOf(item.product_id) !== -1) return true;
    const type = (item.product_type || '').toLowerCase();
    if (type === 'planches à découper' || type === 'couteaux artisanaux') return true;
    return false;
  }
  function isPlancheItem(item) {
    return (item.product_type || '').toLowerCase() === 'planches à découper';
  }

  /* ---------- Gravure hiding + parent line amount boost ---------- */
  function isGravureItem(item) {
    return (item.handle || '') === 'gravure-personnalisee';
  }
  function sumGravureCents(items) {
    return items.filter(isGravureItem).reduce(function (acc, i) { return acc + (i.final_line_price || i.line_price || 0); }, 0);
  }
  function itemHasGravureProp(item) {
    return !!(item.properties && item.properties.Gravure);
  }

  /* ---------- Render ---------- */
  function renderBody(cart) {
    if (!body) return;
    if (cart.item_count === 0) {
      body.innerHTML = emptyStateHtml();
      return;
    }

    // Hide gravure lines, then distribute their price across lines that have a Gravure property.
    const visibleItems = cart.items.filter(function (i) { return !isGravureItem(i); });
    const gravureTotal = sumGravureCents(cart.items);
    const gravureBearers = visibleItems.filter(itemHasGravureProp);
    const perBearerBoost = gravureBearers.length > 0 ? Math.round(gravureTotal / gravureBearers.length) : 0;

    const hasPlanche = visibleItems.some(isPlancheItem);
    const hasBaume = visibleItems.some(function (i) { return (i.handle || '').indexOf('baume-bois') === 0; });

    const itemsHtml = visibleItems.map(function (i) {
      const extra = itemHasGravureProp(i) ? perBearerBoost : 0;
      return lineItemHtml(i, extra);
    }).join('');
    const baumeHtml = hasPlanche && !hasBaume ? baumeUpsellHtml() : '';

    body.innerHTML =
      '<div class="w-commerce-commercecartform" data-cart-form>' +
        '<div class="w-commerce-commercecartlist cart-list" data-cart-items>' + itemsHtml + '</div>' +
        baumeHtml +
        '<div class="w-commerce-commercecartfooter cart-footer">' +
          '<div aria-atomic="true" aria-live="polite" class="w-commerce-commercecartlineitem cart-line-item">' +
            '<div class="sous-total">Sous total</div>' +
            '<div class="w-commerce-commercecartordervalue prix" data-cart-subtotal>' + formatMoney(cart.total_price) + '</div>' +
          '</div>' +
          '<div class="checkout-actions">' +
            '<a href="/checkout" class="w-commerce-commercecartcheckoutbutton checkout-button">' +
              '<span>Passage à la caisse</span>' +
              '<svg class="checkout-arrow" width="18" height="14" viewBox="0 0 18 14" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M1 7h16"/><path d="m11 1 6 6-6 6"/></svg>' +
            '</a>' +
            '<p class="paragraph-xsmall">Frais d\'expédition calculés à l\'étape suivante</p>' +
          '</div>' +
        '</div>' +
      '</div>';
  }

  function lineItemHtml(item, priceBoost) {
    const unique = isUniqueItem(item);
    const displayPrice = (item.final_line_price || 0) + (priceBoost || 0);
    const imageTag = item.image
      ? '<img src="' + item.image.replace(/_(small|compact|medium|large|grande|1024x1024|original)\./, '.') + '" alt="' + esc(item.product_title) + '" class="w-commerce-commercecartitemimage image-cart">'
      : '<img src="" alt="" class="w-commerce-commercecartitemimage image-cart w-dyn-bind-empty">';
    const image = '<a class="cart-item-image-link" href="' + (item.url || '#') + '">' + imageTag + '</a>';

    const compareAt = (item.variant_compare_at_price && item.variant_compare_at_price > item.price)
      ? '<div class="paragraph-small bold red">' + formatMoney(item.variant_compare_at_price * item.quantity) + '</div>'
      : '';

    const qtyBlock = unique ? '' :
      '<div class="cart-qty-wrap">' +
        '<button type="button" class="cart-qty-btn" data-cart-qty-minus aria-label="Diminuer">−</button>' +
        '<input aria-label="Update quantity" class="w-commerce-commercecartquantity cart-quantity-2" required pattern="^[0-9]+$" inputmode="numeric" type="number" name="quantity" autocomplete="off" data-cart-qty-input value="' + item.quantity + '" min="1">' +
        '<button type="button" class="cart-qty-btn" data-cart-qty-plus aria-label="Augmenter">+</button>' +
      '</div>';

    let props = '';
    let gravureInline = '';
    if (item.properties) {
      const parts = [];
      Object.keys(item.properties).forEach(function (k) {
        if (k.indexOf('_') === 0 || !item.properties[k]) return;
        if (k === 'Gravure') {
          gravureInline = '<span class="cart-gravure-inline">· Gravure : <strong>' + esc(item.properties[k]) + '</strong></span>';
          return;
        }
        parts.push(
          '<li class="option">' +
            '<span>' + esc(k) + '</span><span>: </span>' +
            '<span class="text-block-5">' + esc(item.properties[k]) + '</span>' +
          '</li>'
        );
      });
      if (parts.length) props = '<ul class="w-commerce-commercecartoptionlist option-list">' + parts.join('') + '</ul>';
    }

    const shortDescMap = window.DSW_PRODUCT_SHORT_DESC || {};
    const shortDesc = shortDescMap[item.product_id];
    const shortDescHtml = shortDesc ? '<p class="cart-short-desc">' + esc(shortDesc) + '</p>' : '';

    return (
      '<div class="w-commerce-commercecartitem cart-item" data-line-key="' + esc(item.key) + '">' +
        '<div class="image-cart-wrapper">' +
          '<button type="button" class="remove-button w-inline-block" data-cart-remove aria-label="Retirer l\'article">' +
            '<span class="close-text">×</span>' +
          '</button>' +
          image +
        '</div>' +
        '<div class="w-commerce-commercecartiteminfo div-block-23">' +
          '<a class="w-commerce-commercecartproductname" href="' + (item.url || '#') + '">' + esc(item.product_title) + '</a>' +
          shortDescHtml +
          '<div class="cart-price-wrapper">' +
            '<div class="paragraph-small bold">' + formatMoney(displayPrice) + '</div>' +
            compareAt +
            gravureInline +
          '</div>' +
          qtyBlock +
          props +
        '</div>' +
      '</div>'
    );
  }

  function baumeUpsellHtml() {
    const b = window.DSW_BAUME;
    if (!b || !b.variantId) return '';
    return (
      '<div id="baumecartplace" class="baume-cart-item-wrapper" data-cart-baume>' +
        '<div class="baume-cart-item">' +
          '<div class="baume-cart-cover">' +
            (b.image ? '<img loading="lazy" src="' + b.image + '" alt="' + esc(b.title) + '" class="cover">' : '') +
          '</div>' +
          '<div class="baume-cart-infos">' +
            '<div class="div-block-24">' +
              '<div class="text-block-8"><strong>Protégez votre planche dans le temps</strong></div>' +
              '<p class="paragraph-5">Le baume bois naturel nourrit, protège et prolonge la durée de vie de votre planche.</p>' +
            '</div>' +
            '<div class="baume-cart-buy">' +
              '<div class="paragraph-small bold baume-cart-price">' + formatMoney(b.priceCents || 0) + '</div>' +
              '<form action="/cart/add" method="post" data-cart-baume-form class="w-commerce-commerceaddtocartform baume-form">' +
                '<input type="hidden" name="id" value="' + b.variantId + '">' +
                '<input type="hidden" name="quantity" value="1">' +
                '<button type="submit" class="w-commerce-commerceaddtocartbutton add-to-cart">' +
                  '<span class="atc-plus" aria-hidden="true">+</span>' +
                  '<span class="atc-label">Ajouter au panier</span>' +
                '</button>' +
              '</form>' +
            '</div>' +
          '</div>' +
        '</div>' +
      '</div>'
    );
  }

  function emptyStateHtml() {
    return (
      '<div class="w-commerce-commercecartemptystate empty-state">' +
        '<img src="' + (window.DSW_PLANCHE_NORI || '') + '" loading="lazy" alt="" class="planche-cart">' +
        '<div aria-live="polite" class="h3-small">🪵 Votre panier est vide</div>' +
        '<p>Il semble qu\'aucune création ne s\'y soit encore glissée… Découvrez nos planches à découper, pièces uniques façonnées à la main en Alsace.</p>' +
        '<a href="/collections/planches-a-decouper" class="button w-inline-block">' +
          '<p class="button-text">Découvrir la boutique</p>' +
          '<img src="' + (window.DSW_ARROW_BTN || '') + '" loading="lazy" alt="" class="icon-button">' +
        '</a>' +
      '</div>'
    );
  }

  /* ---------- Intercept add-to-cart forms ---------- */
  function interceptAddForms() {
    document.addEventListener('submit', async function (e) {
      if (e.defaultPrevented) return; // another handler already took over
      const form = e.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (!form.matches('form.w-commerce-commerceaddtocartform, form[data-node-type="commerce-add-to-cart-form"]')) return;
      const action = form.getAttribute('action') || '';
      if (action && action.indexOf('/cart/add') === -1) return;
      e.preventDefault();

      // Block duplicate add for unique products (qty capped at 1)
      const idInput = form.querySelector('input[name="id"]');
      const variantId = idInput ? idInput.value : null;
      if (variantId && isUniqueVariantId(variantId) && variantAlreadyInCart(variantId)) {
        openDrawer();
        return;
      }

      const submitBtn = form.querySelector('[type="submit"]');
      if (submitBtn) submitBtn.disabled = true;
      try {
        const formData = new FormData(form);
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          body: formData,
          headers: { Accept: 'application/json' },
        });
        if (!res.ok) {
          const err = await res.json();
          console.error('add to cart error', err);
          return;
        }
        await refreshCart({ open: true });
      } finally {
        if (submitBtn) submitBtn.disabled = false;
      }
    });
  }

  /* ---------- Qty / remove delegates ---------- */
  async function changeLine(key, quantity) {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity }),
    });
    let cart = await res.json();
    cart = await cleanupOrphanGravures(cart);
    renderBody(cart);
    updateCount(cart.item_count);
  }

  function bindDelegates() {
    root.addEventListener('click', function (e) {
      const closeBtn = e.target.closest('[data-cart-close]');
      if (closeBtn) { e.preventDefault(); closeDrawer(); return; }
      const removeBtn = e.target.closest('[data-cart-remove]');
      if (removeBtn) {
        e.preventDefault();
        const line = removeBtn.closest('[data-line-key]');
        if (line) changeLine(line.dataset.lineKey, 0);
        return;
      }
      const plus = e.target.closest('[data-cart-qty-plus]');
      const minus = e.target.closest('[data-cart-qty-minus]');
      if (plus || minus) {
        e.preventDefault();
        const line = e.target.closest('[data-line-key]');
        if (!line) return;
        const input = line.querySelector('[data-cart-qty-input]');
        if (!input) return;
        let q = parseInt(input.value, 10) || 1;
        q = plus ? q + 1 : Math.max(1, q - 1);
        input.value = q;
        changeLine(line.dataset.lineKey, q);
        return;
      }
    });
    panel.addEventListener('click', function (e) {
      if (e.target === panel) closeDrawer();
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && panel.classList.contains('is-open')) closeDrawer();
    });
    // Direct typing in the qty input (debounced)
    let qtyTimer = null;
    root.addEventListener('input', function (e) {
      const input = e.target.closest('[data-cart-qty-input]');
      if (!input) return;
      clearTimeout(qtyTimer);
      qtyTimer = setTimeout(function () {
        const line = input.closest('[data-line-key]');
        if (!line) return;
        const q = Math.max(1, parseInt(input.value, 10) || 1);
        input.value = q;
        changeLine(line.dataset.lineKey, q);
      }, 450);
    });
  }

  /* ---------- Intercept header cart trigger ---------- */
  function interceptCartButtons() {
    document.addEventListener('click', function (e) {
      const trigger = e.target.closest('[data-cart-open], .site-header__cart, .w-commerce-commercecartopenlink, a.cart-button');
      if (!trigger) return;
      if (trigger.closest('[data-cart-drawer]')) return;
      e.preventDefault();
      openDrawer();
    });
  }

  /* ---------- Custom event: dsw:cart:added (fired by custom product forms) ---------- */
  window.addEventListener('dsw:cart:added', function () {
    refreshCart({ open: true });
  });

  /* ---------- Init ---------- */
  interceptAddForms();
  interceptCartButtons();
  bindDelegates();
  refreshCart();
})();
